import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Download, ClipboardList } from "lucide-react";

interface SustainabilityScanLeadOption {
  text: string;
  score: number;
  classification: string;
}

interface SustainabilityScanLeadQA {
  questionId: number;
  section: string;
  topic: string;
  questionText: string;
  selectedOption: SustainabilityScanLeadOption;
}

interface SustainabilityScanLeadCategoryScore {
  category: string;
  score: number;
  percentage: number;
  description: string;
}

interface SustainabilityScanLeadRecommendation {
  category: string;
  questionText: string;
  classification: string;
  recommendation: string;
}

interface SustainabilityScanLead {
  id: string;
  fullName: string;
  company: string;
  email: string;
  fte: number;
  sector: string;
  questionsAndAnswers: SustainabilityScanLeadQA[];
  overallScore: string;
  maxScore: number;
  rating: string;
  categoryScores: SustainabilityScanLeadCategoryScore[];
  recommendations: SustainabilityScanLeadRecommendation[];
  createdAt: string;
}

interface LeadsResponse {
  leads: SustainabilityScanLead[];
  total: number;
  page: number;
  pageSize: number;
}

async function fetchLeads(page: number): Promise<LeadsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  const res = await fetch(`/api/admin/sustainability-scan-leads?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch sustainability scan leads");
  return res.json();
}

const formatDate = (s: string) =>
  new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

export default function AdminSustainabilityScanLeads() {
  const { user } = useAuth();

  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState<SustainabilityScanLead | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/sustainability-scan-leads", page],
    queryFn: () => fetchLeads(page),
    enabled: user?.role === "admin",
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch("/api/admin/sustainability-scan-leads?pageSize=10000", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch sustainability scan leads");
      const { leads: all }: LeadsResponse = await res.json();

      const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
      const header = [
        "ID", "Full Name", "Company", "Email", "FTE", "Sector",
        "Overall Score", "Max Score", "Rating", "Received",
      ].join(";");
      const rows = all.map((l) =>
        [l.id, l.fullName, l.company, l.email, l.fte, l.sector, l.overallScore, l.maxScore, l.rating, formatDate(l.createdAt)]
          .map(String)
          .map(escape)
          .join(";")
      );
      const csv = [header, ...rows].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sustainability-scan-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // export failure is non-critical; the table itself is still usable
    } finally {
      setExporting(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">{"Access Denied"}</h3>
            <p className="text-sm text-slate-500 mt-1">{"You must be a platform administrator to view this page."}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const leads = data?.leads ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
          {"Sustainability Scan Leads"}
        </h1>
        <p className="text-slate-500 mt-2">{"Leads captured from the 180Pi sustainability scan on the marketing site."}</p>
      </div>

      <div className="flex items-center justify-end gap-4">
        <Button variant="outline" className="rounded-2xl" onClick={exportCsv} disabled={exporting}>
          <Download className="h-4 w-4 mr-2" />
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {`${total} Leads`}
            </h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {leads.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{"No leads found."}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Name"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Company"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Email"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Sector"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"FTE"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Rating"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Score"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Received"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide text-right">{"Details"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} className="border-t border-slate-100">
                      <TableCell className="font-semibold text-slate-900">{l.fullName}</TableCell>
                      <TableCell className="text-slate-700">{l.company}</TableCell>
                      <TableCell className="text-slate-700">{l.email}</TableCell>
                      <TableCell className="text-slate-700">{l.sector}</TableCell>
                      <TableCell className="text-slate-700">{l.fte}</TableCell>
                      <TableCell className="text-slate-700">{l.rating}</TableCell>
                      <TableCell className="text-slate-700">{`${l.overallScore} / ${l.maxScore}`}</TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(l.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl"
                          onClick={() => setSelectedLead(l)}
                        >
                          <ClipboardList className="h-4 w-4 text-primary" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {"Previous"}
              </Button>
              <span className="text-sm text-slate-500">
                {`Page ${page} of ${totalPages}`}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {"Next"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selectedLead} onOpenChange={(open) => { if (!open) setSelectedLead(null); }}>
        <DialogContent className="sm:max-w-[600px] rounded-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {"Sustainability Scan Lead"}
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Name"}</span>
                  <span className="font-semibold text-slate-900">{selectedLead.fullName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Company"}</span>
                  <span className="font-semibold text-slate-900">{selectedLead.company}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Email"}</span>
                  <span className="font-semibold text-slate-900">{selectedLead.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"FTE"}</span>
                  <span className="font-semibold text-slate-900">{selectedLead.fte}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Sector"}</span>
                  <span className="font-semibold text-slate-900">{selectedLead.sector}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Rating"}</span>
                  <span className="font-semibold text-slate-900">{selectedLead.rating}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Score"}</span>
                  <span className="font-semibold text-slate-900">{`${selectedLead.overallScore} / ${selectedLead.maxScore}`}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Received"}</span>
                  <span className="font-semibold text-slate-900">{formatDate(selectedLead.createdAt)}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700">{"Category Scores"}</Label>
                <div className="space-y-2">
                  {selectedLead.categoryScores.map((c, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-2xl text-sm">
                      <div className="flex justify-between font-semibold text-slate-900">
                        <span>{c.category}</span>
                        <span>{`${c.score} (${c.percentage}%)`}</span>
                      </div>
                      {c.description && <p className="text-slate-500 mt-1">{c.description}</p>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700">{"Recommendations"}</Label>
                <div className="space-y-2">
                  {selectedLead.recommendations.map((r, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-2xl text-sm">
                      <div className="flex justify-between font-semibold text-slate-900">
                        <span>{r.category}</span>
                        <span className="text-slate-500">{r.classification}</span>
                      </div>
                      <p className="text-slate-700 mt-1">{r.questionText}</p>
                      <p className="text-slate-500 mt-1">{r.recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
